import { Link } from "react-router-dom";
import { Instagram, MessageCircle, MapPin, Mail } from "lucide-react";
import { BRAND, buildWhatsAppLink, NAV_LINKS } from "../lib/constants";

const TikTokIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    width="20"
    height="20"
    {...props}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.42a8.16 8.16 0 0 0 4.77 1.52V6.62a4.85 4.85 0 0 1-1.84-.07Z" />
  </svg>
);

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-[#1f1f1f] bg-[#050505] mt-12"
    >
      {/* Big CTA strip */}
      <div className="border-b border-[#1f1f1f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#CCFF00] mb-3">
              Siap Mulai Custom?
            </p>
            <h3 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase leading-none tracking-tight text-white">
              Buat Apparel <br />
              <span className="text-outline">Versi Kamu</span> Sekarang.
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp-cta"
              className="btn-primary"
            >
              <MessageCircle className="w-5 h-5" />
              Chat Admin
            </a>
            <Link
              to="/custom"
              data-testid="footer-custom-cta"
              className="btn-ghost"
            >
              Cara Order
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Link to="/" className="inline-flex items-baseline gap-2">
            <span className="font-display text-3xl uppercase text-white">
              ACE<span className="text-[#CCFF00]">.</span>
            </span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">
              Sublimasi Nusantara
            </span>
          </Link>
          <p className="mt-4 text-sm text-[#A3A3A3] leading-relaxed max-w-md">
            {BRAND.description}
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a
              href={`https://instagram.com/${BRAND.ig}`}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-ig-link"
              aria-label="Instagram"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#262626] text-white hover:text-[#CCFF00] hover:border-[#CCFF00] transition-colors"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href={`https://tiktok.com/@${BRAND.tiktok}`}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-tiktok-link"
              aria-label="TikTok"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#262626] text-white hover:text-[#CCFF00] hover:border-[#CCFF00] transition-colors"
            >
              <TikTokIcon />
            </a>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-wa-icon"
              aria-label="WhatsApp"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#262626] text-white hover:text-[#CCFF00] hover:border-[#CCFF00] transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#CCFF00] mb-4">
            Navigasi
          </p>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm text-white hover:text-[#CCFF00] transition-colors"
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#CCFF00] mb-4">
            Workshop
          </p>
          <ul className="space-y-4 text-sm text-[#A3A3A3]">
            <li className="flex gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[#CCFF00] shrink-0" />
              <span>
                {BRAND.address.line1}, {BRAND.address.line2}, {BRAND.address.city}, {BRAND.address.province} {BRAND.address.postal}
              </span>
            </li>
            <li className="flex gap-3 items-center">
              <MessageCircle className="w-4 h-4 text-[#CCFF00] shrink-0" />
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-wa-text"
                className="hover:text-white"
              >
                WhatsApp {BRAND.whatsappDisplay}
              </a>
            </li>
            <li className="flex gap-3 items-center">
              <Mail className="w-4 h-4 text-[#CCFF00] shrink-0" />
              <a
                href={`mailto:${BRAND.email}`}
                className="hover:text-white"
                data-testid="footer-email-link"
              >
                {BRAND.email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#1f1f1f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between text-xs text-[#A3A3A3]">
          <p>
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </p>
          <p className="uppercase tracking-widest">
            Made with full sublimation in Wonosobo
          </p>
        </div>
      </div>
    </footer>
  );
}
